// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import BusComponent from '../BusComponent';
import Trigger from 'rc-trigger';
import { focusFirstChild } from '../../helpers/Util';
import AreaSelectorPopup from './AreaSelectorPopup';
import { HelpTourTargets } from '../../enums/HelpTourDefinitions';
import LocationAnalysisType from '../../enums/LocationAnalysisType';

const popupAlignOptions = {
    points: ['tl', 'bl'],
    offset: [0, 10],
};

/**
 * @typedef Props
 * @property {import('../../objects/LocationAnalysisItem').default} locationAnalysisItem
 * @property {string} mapInstanceId
 * @property {boolean} [allowCustomShape]
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {import('../../objects/LocationAnalysisItem').default} locationAnalysisItem
 * @property {boolean} popupVisible
 *
 * @extends {BusComponent<Props, State>}
 */
class AreaSelector extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            locationAnalysisItem: this.props.locationAnalysisItem,
            popupVisible: false,
        };
        this.boundAfterPopupRender = this.afterPopupRender.bind(this);
    }

    componentDidMount() {
        this.bindGluBusEvents({
            LOCATION_ANALYSIS_ITEM_UPDATED: this.onLocationAnalysisItemUpdated,
            CUSTOM_LOCATION_ANALYSIS_VALUE_ADDED: this.onCustomLocationAnalysisValueAdded,
            AREA_SELECTOR_PENDING_SELECTION: this.onPendingSelection,
        });
        this.emit('HELP_TOUR_STEP_LOADED');
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    /**
     * @param {object} payload
     * @param {import('../../objects/LocationAnalysisItem').default} payload.locationAnalysisItem
     */
    onLocationAnalysisItemUpdated = ({ locationAnalysisItem }) => {
        this.setState({
            locationAnalysisItem,
        });
    };

    afterPopupRender = visible => {
        if (visible) {
            focusFirstChild(this.popupContainer);
        }
    };

    handlePopupVisibleChange = nextVisible => {
        this.setState({ popupVisible: nextVisible });
    };

    closePopup = () => {
        this.setState({ popupVisible: false });
    };

    /** @param {{ selection: Set<number> }} payload */
    onPendingSelection = ({ selection }) => {
        this._pendingSelection = selection;
    };

    onCustomLocationAnalysisValueAdded = ({ value, analysisTypeUnit }) => {
        const { locationAnalysisItem } = this.state;
        if (!locationAnalysisItem) return;

        const activeType = locationAnalysisItem.analysisType || LocationAnalysisType.RADIUS;
        if (analysisTypeUnit !== activeType.UNIT) return;

        // Use the pending selection if available, otherwise fall back to the applied selection
        const baseSelection = this._pendingSelection || locationAnalysisItem.selection || new Set();
        this._pendingSelection = null;

        const updatedSelection = new Set([...baseSelection, value]);
        const sortedSelection = [...updatedSelection].sort((a, b) => a - b);

        // Apply the selection so the parent props update before popup remounts
        this.emit('SET_LOCATION_ANALYSIS_TYPE_AND_SELECTION', {
            mapInstanceId: this.props.mapInstanceId,
            analysisTypeId: activeType.ID,
            selection: new Set(sortedSelection),
        });
        this.bus.emit('LOCATION_ANALYSIS_MODE_STARTED', {
            mapInstanceId: this.props.mapInstanceId,
        });
        this.bus.emit('GET_STORAGE_FACILITY_COUNT_REQUEST', {
            mapInstanceId: this.props.mapInstanceId,
        });

        // Reopen the popup so the new chip is visible and selected
        this.setState({ popupVisible: true });
    };

    render() {
        if (!this.state.locationAnalysisItem) {
            return null;
        }
        const { analysisType, selection } = this.state.locationAnalysisItem;
        const activeType = analysisType || LocationAnalysisType.RADIUS;
        const selectionArray = Array.from(selection);

        const isShape = activeType.ID === LocationAnalysisType.POLYGON.ID;
        const unit = isShape ? null : activeType.MAPBOX_ROUTING_PROFILE ? (
            <FormattedMessage id="min" />
        ) : (
            <FormattedMessage
                id="mi"
                values={{
                    count: selectionArray.length > 1 ? selectionArray.length : selectionArray[0],
                }}
            />
        );
        const distance = isShape
            ? activeType.NAME
            : selectionArray.sort((a, b) => a - b).join(', ');

        return (
            <Trigger
                action={['click']}
                popupAlign={popupAlignOptions}
                popupVisible={this.state.popupVisible}
                onPopupVisibleChange={this.handlePopupVisibleChange}
                popup={
                    <div
                        className="menu-popup"
                        ref={e => {
                            this.popupContainer = e;
                        }}
                    >
                        <AreaSelectorPopup
                            key={this.state.popupVisible ? 'open' : 'closed'}
                            analysisType={activeType}
                            selection={new Set(selectionArray)}
                            mapInstanceId={this.props.mapInstanceId}
                            onClose={this.closePopup}
                            allowCustomShape={this.props.allowCustomShape}
                            locationAnalysisItem={this.state.locationAnalysisItem}
                        />
                    </div>
                }
            >
                <div data-tourId={HelpTourTargets.INFO_PANEL_RADIUS_SELECTOR}>
                    <button
                        className="btn-chip btn-chip--insight-panel area-selector"
                        aria-haspopup
                        data-testid="area-selector-button"
                    >
                        <i className="material-icons btn-chip__icon">{activeType.ICON}</i>
                        <span>{distance}</span>
                        {unit}
                    </button>
                </div>
            </Trigger>
        );
    }
}

AreaSelector.propTypes = {
    locationAnalysisItem: PropTypes.object.isRequired,
    mapInstanceId: PropTypes.string.isRequired,
    allowCustomShape: PropTypes.bool,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(AreaSelector);
